import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === "string" ? s.token : "" }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = useState<"checking" | "valid" | "invalid" | "done">("checking");
  const [email, setEmail] = useState<string | null>(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.post<{ valid: boolean; email?: string }>("/api/auth/password-reset/validate", { token })
      .then((r) => {
        if (r.valid) { setEmail(r.email ?? null); setState("valid"); }
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async () => {
    if (pwd.length < 8) { toast.error("Mot de passe trop court (min. 8 caractères)"); return; }
    if (pwd !== pwd2) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setSubmitting(true);
    try {
      await api.post("/api/auth/password-reset/confirm", { token, password: pwd });
      setState("done");
      toast.success("Mot de passe défini avec succès");
    } catch (e: any) {
      toast.error(e?.message ?? "Erreur lors de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* modal */ }}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Définir votre mot de passe</DialogTitle>
          {email && state === "valid" && (
            <DialogDescription>Compte : <strong>{email}</strong></DialogDescription>
          )}
        </DialogHeader>

        {state === "checking" && (
          <div className="py-8 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Vérification du lien…</span>
          </div>
        )}

        {state === "invalid" && (
          <div className="py-6 space-y-3 text-center">
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <p className="font-medium">Lien invalide ou expiré</p>
            <p className="text-sm text-muted-foreground">
              Demandez à un administrateur de vous envoyer un nouveau lien d'activation.
            </p>
            <Button variant="outline" onClick={() => navigate({ to: "/login" })}>Aller à la connexion</Button>
          </div>
        )}

        {state === "valid" && (
          <>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nouveau mot de passe</Label>
                <Input type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} />
                <p className="text-xs text-muted-foreground">8 caractères minimum.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Confirmer le mot de passe</Label>
                <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={submitting || !pwd || !pwd2}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Définir le mot de passe
              </Button>
            </DialogFooter>
          </>
        )}

        {state === "done" && (
          <div className="py-6 space-y-3 text-center">
            <CheckCircle2 className="h-10 w-10 text-success mx-auto" />
            <p className="font-medium">Mot de passe enregistré</p>
            <p className="text-sm text-muted-foreground">Vous pouvez désormais vous connecter à l'intranet.</p>
            <Button onClick={() => navigate({ to: "/login" })}>Se connecter</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
